import { LoadingSpinner } from "@/components/common/LoadingSpinner/LoadingSpinner"
import { toast } from "@/components/common/Toast"
import { Typography } from "@/components/common/Typography/Typography"
import { CreateKnowledgeBaseCategoryDialog } from "@/components/feature/KnowledgebaseFilters/CreateKnowledgeBaseCategoryDialog"
import {
  createDocumentTagMenuEntries,
  DocumentTagBadge,
  type DocumentTagEntry,
} from "@/components/feature/KnowledgebaseFilters/DocumentTagCategoryMenu"
import { DeleteDocumentModal } from "@/components/feature/Modals/DeleteDocumentModal/DeleteDocumentModal"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  useApproveDocumentAutoTag,
  useCreateKnowledgeBaseTag,
  useDeleteKnowledgeBaseTag,
  useGetDocumentTags,
  useGetKnowledgeBaseCategories,
  useRemoveDocumentTag,
  useSetDocumentTags,
  useUpdateDocumentExpiryDate,
  useUpdateKnowledgeBaseTag,
} from "@/hooks/useKnowledgeBaseCategories/useKnowledgeBaseCategories"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { brandColors } from "@/lib/brandColors"
import { knowledgebaseApiCalls } from "@/services/api/knowledgebaseService"
import { KnowledgeBaseCategory } from "@/types/knowledgebaseCategories"
import { classNames } from "@/utils/classNames"
import { RefetchItemsContext } from "@/utils/context/refetchItemsStackedList"
import { downloadFileFromUrl } from "@/utils/downloadUtils"
import { isExcelFileName, isPdfFileName } from "@/utils/fileUtils"
import { knowledgeBaseErrorKey } from "@/utils/knowledgeBaseErrors"
import { isLimitError } from "@/utils/limitErrors"
import {
  IconDownload,
  IconEye,
  IconLoader2,
  IconTrash,
} from "@tabler/icons-react"
import { Plus, Sparkles } from "lucide-react"
import * as React from "react"
import { CategoryTagEditor, getFileTypeIcon } from "./TagDocumentShared"
import {
  buildDocumentTagSavePayload,
  createDraftSelectedTags,
  getPersistedSelectedTagIdsByCategory,
  getPreservedHiddenTagIds,
  groupSelectedTagsByCategory,
  hasPendingTagSelectionChanges,
  mergeNewPersistedSelectedTagIdsByCategory,
  sanitizeDraftSelectedTagIdsByCategory,
} from "./documentTagDraft"

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

interface TagDocumentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  fileId: string
  fileName: string
  expiryDate: string | null
  createdAt: Date
  source: string
  fileSizeBytes?: number
  onViewFile?: () => void
  /** Prevents the sheet from closing when the user clicks outside (e.g. into a sibling preview modal) */
  preventCloseOnOutsideInteraction?: boolean
}

export const TagDocumentDialog: React.FC<TagDocumentDialogProps> = ({
  open,
  onOpenChange,
  fileId,
  fileName,
  expiryDate,
  createdAt,
  source,
  fileSizeBytes,
  onViewFile,
  preventCloseOnOutsideInteraction = false,
}) => {
  const { currentLanguage, translate } = useTranslation()
  const [isCreateCategoryDialogOpen, setIsCreateCategoryDialogOpen] =
    React.useState(false)
  const [editingCategory, setEditingCategory] =
    React.useState<KnowledgeBaseCategory | null>(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false)
  const [isDownloading, setIsDownloading] = React.useState(false)
  const [isUnsavedChangesDialogOpen, setIsUnsavedChangesDialogOpen] =
    React.useState(false)
  const [draftExpiryDate, setDraftExpiryDate] = React.useState<string | null>(
    expiryDate,
  )
  const [draftSelectedTagIdsByCategory, setDraftSelectedTagIdsByCategory] =
    React.useState<Record<string, string[]>>({})

  const { data: categories = [], isPending: isCategoriesPending } =
    useGetKnowledgeBaseCategories(false, open)
  const { data: documentTags, isPending: isDocumentTagsPending } =
    useGetDocumentTags(fileId, open)
  const { mutateAsync: setDocumentTags, isPending: isSavingDocumentTags } =
    useSetDocumentTags()
  const { mutateAsync: updateDocumentExpiryDate, isPending: isUpdatingExpiry } =
    useUpdateDocumentExpiryDate()
  const { mutateAsync: createTag, isPending: isCreatingTag } =
    useCreateKnowledgeBaseTag()
  const { mutateAsync: updateTag } = useUpdateKnowledgeBaseTag()
  const { mutateAsync: deleteTag } = useDeleteKnowledgeBaseTag()
  const { mutateAsync: approveAutoTag } = useApproveDocumentAutoTag()
  const { mutateAsync: removeDocumentTag } = useRemoveDocumentTag()

  const unapprovedAutoTagEntries = React.useMemo(() => {
    if (!documentTags?.categories) return []
    return createDocumentTagMenuEntries(documentTags.categories).filter(
      (entry) => entry.tag.source === "auto" && entry.tag.approvedAt === null,
    )
  }, [documentTags])

  const allTagEntries = React.useMemo(() => {
    if (!documentTags?.categories) return []
    return createDocumentTagMenuEntries(documentTags.categories).filter(
      (entry) =>
        entry.tag.source === "user" ||
        (entry.tag.source === "auto" && entry.tag.approvedAt !== null),
    )
  }, [documentTags])

  const persistedSelectedTagIdsByCategory = React.useMemo(() => {
    return getPersistedSelectedTagIdsByCategory(allTagEntries)
  }, [allTagEntries])

  const didInitDraftRef = React.useRef(false)
  const previousPersistedSelectedTagIdsByCategoryRef = React.useRef(
    persistedSelectedTagIdsByCategory,
  )

  React.useEffect(() => {
    if (!open) {
      didInitDraftRef.current = false
      previousPersistedSelectedTagIdsByCategoryRef.current =
        persistedSelectedTagIdsByCategory
      return
    }
    if (didInitDraftRef.current) return
    if (isDocumentTagsPending) return
    didInitDraftRef.current = true
    setDraftSelectedTagIdsByCategory(persistedSelectedTagIdsByCategory)
    setDraftExpiryDate(expiryDate)
  }, [
    expiryDate,
    fileId,
    open,
    isDocumentTagsPending,
    persistedSelectedTagIdsByCategory,
  ])

  const preservedHiddenTagIds = React.useMemo(
    () => getPreservedHiddenTagIds(allTagEntries, categories),
    [allTagEntries, categories],
  )

  React.useEffect(() => {
    if (!open) return
    setDraftSelectedTagIdsByCategory((current) => {
      const mergedSelections = mergeNewPersistedSelectedTagIdsByCategory({
        currentSelections: current,
        previousPersistedSelectedTagIdsByCategory:
          previousPersistedSelectedTagIdsByCategoryRef.current,
        persistedSelectedTagIdsByCategory,
      })

      previousPersistedSelectedTagIdsByCategoryRef.current =
        persistedSelectedTagIdsByCategory

      return sanitizeDraftSelectedTagIdsByCategory({
        currentSelections: mergedSelections,
        categories,
        persistedSelectedTagIdsByCategory,
      })
    })
  }, [open, categories, persistedSelectedTagIdsByCategory])

  const draftSelectedTags = React.useMemo<DocumentTagEntry[]>(() => {
    return createDraftSelectedTags({
      categories,
      draftSelectedTagIdsByCategory,
      allTagEntries,
      currentLanguage,
    })
  }, [
    currentLanguage,
    draftSelectedTagIdsByCategory,
    categories,
    allTagEntries,
  ])

  const selectedTagsByCategory = React.useMemo(() => {
    return groupSelectedTagsByCategory(draftSelectedTags)
  }, [draftSelectedTags])

  const hasPendingTagChanges = React.useMemo(() => {
    return hasPendingTagSelectionChanges({
      categories,
      draftSelectedTagIdsByCategory,
      persistedSelectedTagIdsByCategory,
    })
  }, [
    draftSelectedTagIdsByCategory,
    persistedSelectedTagIdsByCategory,
    categories,
  ])

  const hasPendingChanges =
    draftExpiryDate !== expiryDate || hasPendingTagChanges

  const handleSelectionChange = React.useCallback(
    (categoryId: string, values: string[]) => {
      setDraftSelectedTagIdsByCategory((current) => ({
        ...current,
        [categoryId]: values,
      }))
    },
    [],
  )

  const handleCreateTag = React.useCallback(
    async (
      category: KnowledgeBaseCategory,
      tagName: string,
    ): Promise<string | undefined> => {
      if (!tagName.trim()) {
        toast.error(translate("KNOWLEDGEBASE/NAME_INVALID_ERROR"))
        return
      }
      try {
        const createdTag = await createTag({
          categoryId: category.id,
          data: { displayNameEn: tagName.trim() },
        })
        toast.success(
          translate("KNOWLEDGEBASE/TAG_CREATE_SUCCESS", { tag: tagName }),
        )
        return createdTag.id
      } catch (error) {
        if (!isLimitError(error)) {
          toast.error(
            translate(
              knowledgeBaseErrorKey(error, "KNOWLEDGEBASE/TAG_CREATE_ERROR"),
              { name: tagName },
            ),
          )
        }
      }
    },
    [createTag, translate],
  )

  const handleRenameTag = React.useCallback(
    async (tagId: string, categoryId: string, newName: string) => {
      if (!newName.trim()) {
        toast.error(translate("KNOWLEDGEBASE/NAME_INVALID_ERROR"))
        return
      }
      try {
        await updateTag({
          categoryId,
          tagId,
          data: { displayNameEn: newName.trim() },
        })
        toast.success(
          translate("KNOWLEDGEBASE/TAG_UPDATE_SUCCESS", { tag: newName }),
        )
      } catch (error) {
        if (!isLimitError(error)) {
          toast.error(
            translate(
              knowledgeBaseErrorKey(error, "KNOWLEDGEBASE/TAG_UPDATE_ERROR"),
              { name: newName },
            ),
          )
        }
      }
    },
    [updateTag, translate],
  )

  const handleDeleteTag = React.useCallback(
    async (tagId: string, categoryId: string) => {
      try {
        await deleteTag({ categoryId, tagId })
        toast.success(translate("KNOWLEDGEBASE/TAG_DELETE_ACTION"))
      } catch {
        toast.error(translate("KNOWLEDGEBASE/TAG_CREATE_ERROR"))
      }
    },
    [deleteTag, translate],
  )

  const handleCategoryDialogOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      setIsCreateCategoryDialogOpen(nextOpen)
      if (!nextOpen) setEditingCategory(null)
    },
    [],
  )

  const handleOpenCreateCategory = React.useCallback(() => {
    setEditingCategory(null)
    setIsCreateCategoryDialogOpen(true)
  }, [])

  const handleEditCategory = React.useCallback(
    (category: KnowledgeBaseCategory) => {
      setEditingCategory(category)
      setIsCreateCategoryDialogOpen(true)
    },
    [],
  )

  const handleCategoryDeleted = React.useCallback((categoryId: string) => {
    setDraftSelectedTagIdsByCategory((current) => {
      if (!(categoryId in current)) return current
      const nextSelections = { ...current }
      delete nextSelections[categoryId]
      return nextSelections
    })
  }, [])

  const handleRemoveTag = React.useCallback((entry: DocumentTagEntry) => {
    setDraftSelectedTagIdsByCategory((current) => {
      const categoryId = entry.category.categoryId
      const existing = current[categoryId] ?? []
      return {
        ...current,
        [categoryId]: existing.filter((id) => id !== entry.tag.tagId),
      }
    })
  }, [])

  const handleApproveAllAutoTags = React.useCallback(async () => {
    try {
      await Promise.all(
        unapprovedAutoTagEntries.map((entry) =>
          approveAutoTag({ fileId, tagId: entry.tag.tagId }),
        ),
      )
      // Optimistically add approved tags to the draft immediately so they
      // appear in their category without waiting for the query refetch.
      setDraftSelectedTagIdsByCategory((current) => {
        const next = { ...current }
        for (const entry of unapprovedAutoTagEntries) {
          const categoryId = entry.category.categoryId
          const existing = next[categoryId] ?? []
          if (!existing.includes(entry.tag.tagId)) {
            next[categoryId] = [...existing, entry.tag.tagId]
          }
        }
        return next
      })
      toast.success(translate("KNOWLEDGEBASE/AUTO_TAG_APPROVE_ALL_SUCCESS"))
    } catch (error) {
      if (!isLimitError(error)) {
        toast.error(translate("KNOWLEDGEBASE/AUTO_TAG_APPROVE_ALL_ERROR"))
      }
    }
  }, [approveAutoTag, fileId, unapprovedAutoTagEntries, translate])

  const handleRejectAllAutoTags = React.useCallback(async () => {
    try {
      await Promise.all(
        unapprovedAutoTagEntries.map((entry) =>
          removeDocumentTag({ fileId, tagId: entry.tag.tagId }),
        ),
      )
      toast.success(translate("KNOWLEDGEBASE/AUTO_TAG_REJECT_ALL_SUCCESS"))
    } catch {
      toast.error(translate("KNOWLEDGEBASE/AUTO_TAG_REJECT_ALL_ERROR"))
    }
  }, [removeDocumentTag, fileId, unapprovedAutoTagEntries, translate])

  const handleSave = React.useCallback(async () => {
    const { nextTagIds, removedApprovedAutoTagIds } =
      buildDocumentTagSavePayload({
        documentTags,
        draftSelectedTagIdsByCategory,
        preservedHiddenTagIds,
      })
    const operations: Promise<unknown>[] = []
    try {
      if (hasPendingTagChanges) {
        operations.push(
          setDocumentTags({ fileId, data: { tagIds: nextTagIds } }),
        )
      }
      // Delete approved auto tags that were removed by the user
      for (const tagId of removedApprovedAutoTagIds) {
        operations.push(removeDocumentTag({ fileId, tagId }))
      }
      if (draftExpiryDate !== expiryDate) {
        operations.push(
          updateDocumentExpiryDate({
            fileId,
            data: { expiryDate: draftExpiryDate },
          }),
        )
      }
      await Promise.all(operations)
      toast.success(translate("KNOWLEDGEBASE/FILE_TAG_DIALOG_UPDATE_SUCCESS"))
      onOpenChange(false)
    } catch (error) {
      if (!isLimitError(error)) {
        toast.error(translate("KNOWLEDGEBASE/FILE_TAG_DIALOG_UPDATE_ERROR"))
      }
    }
  }, [
    draftExpiryDate,
    draftSelectedTagIdsByCategory,
    expiryDate,
    fileId,
    documentTags,
    hasPendingTagChanges,
    onOpenChange,
    preservedHiddenTagIds,
    removeDocumentTag,
    setDocumentTags,
    translate,
    updateDocumentExpiryDate,
  ])

  const isLoading = isCategoriesPending || isDocumentTagsPending
  const isSaving = isSavingDocumentTags || isUpdatingExpiry

  const handleDownload = React.useCallback(async () => {
    setIsDownloading(true)
    try {
      const fileData = await knowledgebaseApiCalls.getFile(fileId)
      if (!fileData.url) throw new Error("File URL not available")
      await downloadFileFromUrl(fileData.url, fileData.fileName)
      toast.success(translate("KNOWLEDGEBASE/DOWNLOAD_FILE_SUCCESS"))
    } catch {
      toast.error(translate("KNOWLEDGEBASE/PREVIEW_FILE_ERROR"))
    } finally {
      setIsDownloading(false)
    }
  }, [fileId, translate])

  const isPreviewableFile = isPdfFileName(fileName) || isExcelFileName(fileName)

  const handleSheetOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && hasPendingChanges) {
        setIsUnsavedChangesDialogOpen(true)
        return
      }
      onOpenChange(nextOpen)
    },
    [hasPendingChanges, onOpenChange],
  )

  const handleDiscardAndClose = React.useCallback(() => {
    setIsUnsavedChangesDialogOpen(false)
    onOpenChange(false)
  }, [onOpenChange])

  const handleSaveAndClose = React.useCallback(async () => {
    setIsUnsavedChangesDialogOpen(false)
    await handleSave()
  }, [handleSave])

  const FileIcon = getFileTypeIcon(fileName)

  return (
    <>
      <Dialog
        open={isUnsavedChangesDialogOpen}
        onOpenChange={setIsUnsavedChangesDialogOpen}
      >
        <DialogContent className="z-70 sm:max-w-sm" overlayClassName="z-70">
          <DialogHeader>
            <DialogTitle>
              {translate("KNOWLEDGEBASE/UNSAVED_CHANGES_TITLE")}
            </DialogTitle>
            <DialogDescription>
              {translate("KNOWLEDGEBASE/UNSAVED_CHANGES_DESC")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleDiscardAndClose}
            >
              {translate("KNOWLEDGEBASE/UNSAVED_CHANGES_DISCARD")}
            </Button>
            <Button
              type="button"
              onClick={() => void handleSaveAndClose()}
              disabled={isSaving}
            >
              {isSaving ? <LoadingSpinner size={14} className="mr-1" /> : null}
              {translate("AI_CHAT/HISTORY/SAVE")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <CreateKnowledgeBaseCategoryDialog
        open={isCreateCategoryDialogOpen}
        onOpenChange={handleCategoryDialogOpenChange}
        category={editingCategory}
        onCategoryDeleted={handleCategoryDeleted}
      />
      <Sheet open={open} onOpenChange={handleSheetOpenChange} modal={false}>
        {open && !preventCloseOnOutsideInteraction && (
          <div
            className="fixed inset-0 z-50 bg-black/80"
            onClick={() => handleSheetOpenChange(false)}
          />
        )}
        <SheetContent
          side="right"
          className="flex flex-col bg-card p-0 sm:max-w-[500px]"
          onInteractOutside={(e) => {
            // Prevent closing when interacting with portaled dialogs or the sibling preview modal
            if (
              isCreateCategoryDialogOpen ||
              isDeleteModalOpen ||
              isUnsavedChangesDialogOpen ||
              preventCloseOnOutsideInteraction
            ) {
              e.preventDefault()
            }
          }}
        >
          <RefetchItemsContext.Provider value={() => onOpenChange(false)}>
            {isDeleteModalOpen && (
              <DeleteDocumentModal
                fileId={fileId}
                setIsDeleteModalOpen={setIsDeleteModalOpen}
              />
            )}
          </RefetchItemsContext.Provider>

          <div className="flex min-h-0 flex-1 overflow-hidden">
            {/* Form panel */}
            <div className="flex w-full min-w-0 flex-col">
              {/* Header */}
              <SheetHeader className="border-b px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="shrink-0 text-muted-foreground">
                    <FileIcon size={28} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <SheetTitle className="truncate text-base">
                      {fileName}
                    </SheetTitle>
                    <SheetDescription className="mt-0.5 text-xs">
                      {translate("KNOWLEDGEBASE/FILTER_SOURCE_UPLOADED")}{" "}
                      {createdAt.toLocaleDateString()}
                    </SheetDescription>
                  </div>
                </div>
              </SheetHeader>

              <div className="space-y-10 flex-1 overflow-y-auto px-6 py-2">
                {/* Properties */}
                <section>
                  <Typography
                    variant="textXS"
                    className="mb-3 font-semibold tracking-wide text-muted-foreground uppercase"
                  >
                    {translate("KNOWLEDGEBASE/FILE_DETAILS_TITLE")}
                  </Typography>
                  <div className="divide-y divide-border rounded-lg border border-border">
                    <div className="flex items-center justify-between px-4 py-2.5">
                      <Typography
                        variant="textS"
                        className="text-muted-foreground"
                      >
                        {translate("KNOWLEDGEBASE/FILTER_SOURCE")}
                      </Typography>
                      <div className="rounded bg-grey4 px-2 py-0.5">
                        <Typography variant="textXS" className="text-grey5">
                          {source === "API" ? "APP" : source}
                        </Typography>
                      </div>
                    </div>
                    {fileSizeBytes != null && (
                      <div className="flex items-center justify-between px-4 py-2.5">
                        <Typography
                          variant="textS"
                          className="text-muted-foreground"
                        >
                          {translate("KNOWLEDGEBASE/FILE_SIZE_LABEL")}
                        </Typography>
                        <Typography variant="textS">
                          {formatFileSize(fileSizeBytes)}
                        </Typography>
                      </div>
                    )}
                    <div className="flex items-center justify-between px-4 py-2.5">
                      <Typography
                        variant="textS"
                        className="text-muted-foreground"
                      >
                        {translate("KNOWLEDGEBASE/EXPIRY_DATE_LABEL")}
                      </Typography>
                      <div className="flex items-center gap-1">
                        <Input
                          type="date"
                          value={draftExpiryDate ?? ""}
                          onChange={(event) =>
                            setDraftExpiryDate(event.target.value || null)
                          }
                          className="h-7 w-auto text-xs scheme-dark"
                          disabled={isSaving}
                        />
                      </div>
                    </div>
                  </div>
                </section>

                {/* Tags */}
                <section>
                  <div className="mb-3 flex items-center justify-between">
                    <Typography
                      variant="textXS"
                      className="font-semibold tracking-wide text-muted-foreground uppercase"
                    >
                      {translate("KNOWLEDGEBASE/MANAGE_TAGS")}
                    </Typography>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 gap-1 px-2 text-xs"
                      onClick={handleOpenCreateCategory}
                    >
                      <Plus className="size-3" />
                      {translate("KNOWLEDGEBASE/CATEGORY_CREATE_TRIGGER")}
                    </Button>
                  </div>
                  {isLoading ? (
                    <div className="flex min-h-40 items-center justify-center">
                      <LoadingSpinner size={32} />
                    </div>
                  ) : categories.length > 0 ? (
                    <div
                      className={classNames(
                        "space-y-5",
                        isSaving ? "opacity-70" : "",
                      )}
                    >
                      {unapprovedAutoTagEntries.length > 0 && (
                        <div
                          className="rounded-lg px-4 py-3"
                          style={{
                            background:
                              "linear-gradient(135deg, hsl(56 100% 63% / 0.12), hsl(240 3% 32% / 0.25))",
                          }}
                        >
                          <div className="mb-2.5 flex items-center gap-1.5">
                            <Sparkles className="size-3.5 text-primary" />
                            <Typography
                              variant="textXS"
                              className="font-semibold text-primary"
                            >
                              {translate(
                                "KNOWLEDGEBASE/AUTO_TAG_SUGGESTIONS_TITLE",
                              )}{" "}
                              ({unapprovedAutoTagEntries.length})
                            </Typography>
                          </div>
                          <div className="mb-3 flex flex-wrap gap-1.5">
                            {unapprovedAutoTagEntries.map((entry) => (
                              <DocumentTagBadge
                                key={entry.tag.id}
                                entry={entry}
                                onApprove={async () => {
                                  try {
                                    await approveAutoTag({
                                      fileId,
                                      tagId: entry.tag.tagId,
                                    })
                                    setDraftSelectedTagIdsByCategory(
                                      (current) => {
                                        const categoryId =
                                          entry.category.categoryId
                                        const existing =
                                          current[categoryId] ?? []
                                        if (existing.includes(entry.tag.tagId))
                                          return current
                                        return {
                                          ...current,
                                          [categoryId]: [
                                            ...existing,
                                            entry.tag.tagId,
                                          ],
                                        }
                                      },
                                    )
                                  } catch (error) {
                                    if (!isLimitError(error)) {
                                      toast.error(
                                        translate(
                                          "KNOWLEDGEBASE/AUTO_TAG_APPROVE_ERROR",
                                        ),
                                      )
                                    }
                                  }
                                }}
                                onReject={async () => {
                                  try {
                                    await removeDocumentTag({
                                      fileId,
                                      tagId: entry.tag.tagId,
                                    })
                                  } catch {
                                    toast.error(
                                      translate(
                                        "KNOWLEDGEBASE/AUTO_TAG_REJECT_ERROR",
                                      ),
                                    )
                                  }
                                }}
                              />
                            ))}
                          </div>
                          <div className="flex items-center justify-end gap-2 border-t border-white/10 pt-2.5">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                              onClick={() => void handleRejectAllAutoTags()}
                            >
                              {translate("KNOWLEDGEBASE/AUTO_TAG_REJECT_ALL")}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              className="h-6 px-2 text-xs"
                              onClick={() => void handleApproveAllAutoTags()}
                            >
                              {translate("KNOWLEDGEBASE/AUTO_TAG_APPROVE_ALL")}
                            </Button>
                          </div>
                        </div>
                      )}
                      {categories.map((category) => (
                        <CategoryTagEditor
                          key={category.id}
                          category={category}
                          selectedEntries={
                            selectedTagsByCategory[category.id] ?? []
                          }
                          selectedTagIds={
                            draftSelectedTagIdsByCategory[category.id] ?? []
                          }
                          isCreatingTag={isCreatingTag}
                          onEditCategory={handleEditCategory}
                          onCreateTag={handleCreateTag}
                          onSelectionChange={(values) =>
                            handleSelectionChange(category.id, values)
                          }
                          onRemoveTag={handleRemoveTag}
                          onRenameTag={handleRenameTag}
                          onDeleteTag={handleDeleteTag}
                        />
                      ))}
                    </div>
                  ) : (
                    <Typography as="p" variant="textXS" className="text-grey5">
                      {translate(
                        "KNOWLEDGEBASE/FILE_TAG_DIALOG_EMPTY_CATEGORIES",
                      )}
                    </Typography>
                  )}
                </section>
              </div>

              <SheetFooter className="border-t px-6 py-4">
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isPreviewableFile && onViewFile && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        data-testid="tag-document-dialog-view-file"
                        onClick={onViewFile}
                      >
                        <IconEye size={14} />
                        {translate("KNOWLEDGEBASE/VIEW_FILE")}
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void handleDownload()}
                      disabled={isDownloading}
                      aria-label={translate(
                        "HISTORY/FILE_ARIA_LABEL_DOWNLOAD_BUTTON",
                      )}
                    >
                      {isDownloading ? (
                        <IconLoader2 size={14} className="animate-spin" />
                      ) : (
                        <IconDownload size={14} />
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid="tag-document-dialog-delete-file"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setIsDeleteModalOpen(true)}
                    >
                      <IconTrash size={14} />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      onClick={() => handleSheetOpenChange(false)}
                      variant="outline"
                      disabled={isSaving}
                    >
                      {translate("AI_CHAT/HISTORY/CANCEL")}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => void handleSave()}
                      disabled={!hasPendingChanges || isSaving}
                    >
                      {isSaving ? (
                        <LoadingSpinner
                          size={16}
                          className="mr-2"
                          color={brandColors.grey2}
                        />
                      ) : null}
                      {translate("AI_CHAT/HISTORY/SAVE")}
                    </Button>
                  </div>
                </div>
              </SheetFooter>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}
